import type { KeyboardAction, KeyboardActionId } from "~/keyboard/actionCatalog";

export type HelpSectionId = "global" | "grid" | "navigation" | "page";

export interface HelpEntry {
	bindings: string[];
	id: string;
	label: string;
}

export interface HelpSection {
	entries: HelpEntry[];
	id: HelpSectionId;
	title: string;
}

export interface BuildHelpSectionsInput {
	actions: KeyboardAction[];
	registeredActionIds: KeyboardActionId[];
	resolveBindings: (action: KeyboardAction) => string[];
}

export const GRID_BUILTIN_SHORTCUTS: HelpEntry[] = [
	{ bindings: ["↑↓←→"], id: "grid.move", label: "Move cell focus" },
	{ bindings: ["mod+c"], id: "grid.copy", label: "Copy selected cells" },
	{ bindings: ["Enter", "F2"], id: "grid.edit", label: "Edit / commit cell" },
	{ bindings: ["Escape"], id: "grid.cancel", label: "Cancel cell edit" },
	{ bindings: ["Space"], id: "grid.select", label: "Toggle row selection" },
	{ bindings: ["mod+a"], id: "grid.selectAll", label: "Select all rows" },
	{ bindings: ["mod+z", "mod+y"], id: "grid.undo", label: "Undo / redo (while editing a cell)" }
];

const isNavigationAction = (action: KeyboardAction): boolean => action.id.startsWith("nav.");

const toHelpEntry = (action: KeyboardAction, resolveBindings: (action: KeyboardAction) => string[]): HelpEntry => ({
	bindings: resolveBindings(action),
	id: action.id,
	label: action.label
});

export const buildHelpSections = ({
	actions,
	registeredActionIds,
	resolveBindings
}: BuildHelpSectionsInput): HelpSection[] => {
	const registered = new Set(registeredActionIds);
	const visible = actions.filter((action) => registered.has(action.id));

	const globalEntries = visible
		.filter((action) => action.scope === "global" && !isNavigationAction(action))
		.map((action) => toHelpEntry(action, resolveBindings));

	const navigationEntries = visible.filter(isNavigationAction).map((action) => toHelpEntry(action, resolveBindings));

	const pageEntries = visible
		.filter((action) => action.scope === "page" || action.scope === "module" || action.scope === "panel")
		.map((action) => toHelpEntry(action, resolveBindings));

	return [
		{ entries: globalEntries, id: "global", title: "Global" },
		{ entries: navigationEntries, id: "navigation", title: "Navigation" },
		{ entries: pageEntries, id: "page", title: "This page" },
		{ entries: GRID_BUILTIN_SHORTCUTS, id: "grid", title: "Grid built-ins" }
	];
};

const entryMatchesQuery = (entry: HelpEntry, query: string): boolean =>
	entry.label.toLowerCase().includes(query) || entry.bindings.some((binding) => binding.toLowerCase().includes(query));

export const filterHelpSections = (sections: HelpSection[], search: string): HelpSection[] => {
	const query = search.trim().toLowerCase();
	const matched = query
		? sections.map((section) => ({
				...section,
				entries: section.entries.filter((entry) => entryMatchesQuery(entry, query))
			}))
		: sections;
	return matched.filter((section) => section.entries.length > 0);
};

export default buildHelpSections;
